/**
 * Shared pieces for the SEO pages, so the six of them read as one product:
 * the same tile, panel, chart, status pill and range switch everywhere.
 *
 * Tile and chart follow the admin Dashboard (CompactKpi / TrendChart) rather
 * than inventing a second look.
 */
import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const numberFmt = new Intl.NumberFormat('en-US');

export const fmtNumber = (n: number | null | undefined) => (n == null ? '—' : numberFmt.format(n));

/**
 * One colour per marketplace, everywhere, in this order. From the Dashboard's
 * palette; checked for colour-blind separation as a set. Charts using more than
 * one always carry a legend or a label, so colour is never the only cue.
 */
export const SITE_COLOR: Record<string, string> = {
  feelyfeely: '#4F46E5',
  jiji: '#059669',
  meqasa: '#D97706',
};
export const SITE_LABEL: Record<string, string> = {
  feelyfeely: 'FeelyFeely',
  jiji: 'Jiji',
  meqasa: 'Meqasa',
  tonaton: 'Tonaton',
  jumia: 'Jumia',
};
/** A series that is not a marketplace: one neutral, so it never reads as one. */
export const NEUTRAL_SERIES = '#64748B';
export const BAD_SERIES = '#E11D48';

type Tone = 'neutral' | 'good' | 'bad';

const TONE_ICON: Record<Tone, string> = {
  neutral: 'bg-gray-50 text-gray-600',
  good: 'bg-emerald-50 text-emerald-700',
  bad: 'bg-rose-50 text-rose-700',
};

export function SeoKpi({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'neutral',
  to,
  loading,
}: {
  label: string;
  value: string | number | null | undefined;
  hint?: string;
  icon: ComponentType<{ className?: string }>;
  tone?: Tone;
  /** Makes the tile a link to the page that explains the number. */
  to?: string;
  loading?: boolean;
}) {
  const frame = cn(
    'flex min-w-0 items-center gap-3 rounded-lg border border-gray-200 bg-white px-3.5 py-3 text-left shadow-sm',
    to && 'no-underline transition-colors hover:border-gray-300 hover:bg-gray-50',
  );
  const body = (
    <>
      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', TONE_ICON[tone])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
        {loading ? (
          <Skeleton className="mt-1 h-6 w-20" />
        ) : (
          <p
            className={cn(
              'truncate text-xl font-semibold tabular-nums text-gray-900',
              tone === 'bad' && 'text-rose-700',
            )}
          >
            {typeof value === 'number' ? fmtNumber(value) : (value ?? '—')}
          </p>
        )}
        {hint && <p className="truncate text-[11px] text-gray-500">{hint}</p>}
      </div>
    </>
  );
  return to ? (
    <Link to={to} className={frame}>
      {body}
    </Link>
  ) : (
    <div className={frame}>{body}</div>
  );
}

export function SeoKpiRow({ children, cols = 4 }: { children: ReactNode; cols?: 3 | 4 | 5 }) {
  return (
    <div
      className={cn(
        'grid gap-3 sm:grid-cols-2',
        cols === 3 && 'lg:grid-cols-3',
        cols === 4 && 'lg:grid-cols-4',
        cols === 5 && 'lg:grid-cols-5',
      )}
    >
      {children}
    </div>
  );
}

/** A titled card. Tables go edge to edge (`flush`); everything else is padded. */
export function SeoPanel({
  title,
  subtitle,
  action,
  flush,
  className,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  flush?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm', className)}>
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {action}
      </header>
      <div className={cn(flush ? 'overflow-x-auto' : 'px-4 py-3')}>{children}</div>
    </section>
  );
}

/** Table head and cell classes shared by every SEO table. */
export const TH = 'px-4 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500';
export const TD = 'px-4 py-2.5';
export const TR = 'border-t border-gray-100';

export function RangeTabs<T extends number | string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-md px-3 py-1 text-sm font-medium transition-colors',
            value === o.value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const VERDICT = {
  pass: { icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', label: 'Pass' },
  warn: { icon: AlertTriangle, className: 'bg-amber-50 text-amber-800 ring-amber-600/20', label: 'Warning' },
  fail: { icon: XCircle, className: 'bg-rose-50 text-rose-700 ring-rose-600/20', label: 'Fail' },
} as const;

/** A state, as an icon and a word: never colour alone. */
export function StatusPill({ status, label }: { status: keyof typeof VERDICT; label?: string }) {
  const v = VERDICT[status];
  const Icon = v.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        v.className,
      )}
    >
      <Icon className="h-3 w-3" />
      {label ?? v.label}
    </span>
  );
}

export interface TrendSeries {
  key: string;
  name: string;
  color: string;
  kind?: 'line' | 'bar';
  stack?: string;
}

/**
 * A chart over time. One axis only: series on different scales go in separate
 * charts (see `small`), never on a second axis.
 */
export function SeoTrend({
  data,
  series,
  xKey = 'day',
  height = 208,
  small,
  format = fmtNumber,
}: {
  data: object[];
  series: TrendSeries[];
  xKey?: string;
  height?: number;
  /** A compact panel for small multiples: no legend, fewer ticks. */
  small?: boolean;
  format?: (v: number) => string;
}) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-gray-400" style={{ height }}>
        No readings in this period yet.
      </div>
    );
  }
  // A single reading has no line to draw; say so rather than show an empty plot.
  const lonely = data.length === 1;
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 10, fill: '#6b7280' }}
            tickFormatter={(v: string) => (typeof v === 'string' && v.length >= 10 ? v.slice(5) : v)}
            minTickGap={small ? 40 : 24}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 10, fill: '#6b7280' }}
            tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${Math.round(v / 100) / 10}k` : String(v))}
            width={small ? 34 : 40}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#e5e7eb' }}
            formatter={(v: number, name: string) => [format(v), name]}
          />
          {!small && series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />}
          {series.map((s) =>
            s.kind === 'bar' ? (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                fill={s.color}
                stackId={s.stack}
                maxBarSize={22}
                radius={s.stack ? undefined : [3, 3, 0, 0]}
              />
            ) : (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={lonely ? { r: 4 } : false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            ),
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** A labelled horizontal bar for comparing a handful of shares (0–100). */
export function ShareBar({
  label,
  value,
  color = NEUTRAL_SERIES,
  emphasis,
}: {
  label: string;
  value: number;
  color?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className={cn('w-24 shrink-0 text-sm', emphasis ? 'font-semibold text-gray-900' : 'text-gray-700')}>
        {label}
      </div>
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(0, Math.min(100, value))}%`, backgroundColor: color }}
        />
      </div>
      <div className="w-10 shrink-0 text-right text-sm tabular-nums text-gray-900">{value}%</div>
    </div>
  );
}

export function SeoNote({ children }: { children: ReactNode }) {
  return <p className="text-xs leading-relaxed text-gray-500">{children}</p>;
}

export function SeoError({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
      <XCircle className="h-4 w-4 shrink-0" />
      {children}
    </div>
  );
}
